import React, { useCallback, useEffect, useMemo, useState } from "react";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink } from "react-router-dom";
import { orderList, orderGet, orderCancel } from "../../api/bseApi";
import {
  buildOrderListPayload,
  buildOrderGetPayload,
  buildOrderCancelPayload,
  parseOrderListResponse,
  parseOrderGetResponse,
  extractBseApiError,
  isOrderCancellable,
} from "../../utils/bseOrder";

const TEAL = "#06b6a4";
const TEST_CLIENT_CODE = "DematUCCTest9";
const DEFAULT_MEMBER_ID = process.env.REACT_APP_BSE_MEMBER_ID || "63164";

const inputStyle = {
  borderRadius: "8px",
  border: `1px solid ${TEAL}`,
  padding: "8px 12px",
  fontSize: "14px",
  width: "100%",
  background: "#fff",
};

const radioStyle = (checked) => ({
  width: 18,
  height: 18,
  accentColor: TEAL,
  cursor: "pointer",
});

function formatOrderDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAmount(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "-";
  return `₹${n.toLocaleString("en-IN")}`;
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (["done", "matched", "success"].includes(s)) return "bg-success";
  if (["received", "pending", "open"].includes(s)) return "bg-warning text-dark";
  if (["failed", "rejected", "cancelled"].includes(s)) return "bg-danger";
  return "bg-secondary";
}

function formatStatusLabel(status) {
  const s = String(status || "-");
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function DetailSection({ title, icon, children }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: "10px",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "10px 16px",
          background: "#f8fafc",
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {icon && (
          <i className={`fa ${icon}`} style={{ color: TEAL, fontSize: "14px" }} aria-hidden />
        )}
        <span style={{ fontWeight: "600", fontSize: "13px", color: "#334155", letterSpacing: "0.02em" }}>
          {title}
        </span>
      </div>
      <div style={{ padding: "14px 16px" }}>{children}</div>
    </div>
  );
}

function DetailItem({ label, value, mono, highlight }) {
  return (
    <div>
      <div style={{ fontSize: "11px", fontWeight: "500", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "4px" }}>
        {label}
      </div>
      <div
        style={{
          fontSize: highlight ? "18px" : "14px",
          fontWeight: highlight ? "700" : "500",
          color: highlight ? TEAL : "#0f172a",
          fontFamily: mono ? "ui-monospace, monospace" : "inherit",
          wordBreak: "break-word",
        }}
      >
        {value || "-"}
      </div>
    </div>
  );
}

function OrderHistoryTimeline({ history }) {
  if (!history?.length) return null;
  return (
    <div style={{ position: "relative", paddingLeft: "24px" }}>
      <div
        style={{
          position: "absolute",
          left: "7px",
          top: "6px",
          bottom: "6px",
          width: "2px",
          background: "#e2e8f0",
        }}
      />
      {history.map((h, i) => (
        <div key={`${h.event_status}-${i}`} style={{ position: "relative", paddingBottom: i < history.length - 1 ? "18px" : 0 }}>
          <div
            style={{
              position: "absolute",
              left: "-20px",
              top: "4px",
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              background: i === 0 ? TEAL : "#fff",
              border: `2px solid ${i === 0 ? TEAL : "#cbd5e1"}`,
              zIndex: 1,
            }}
          />
          <div style={{ fontSize: "13px", fontWeight: "600", color: "#334155" }}>
            {formatStatusLabel(h.event_status)}
          </div>
          <div style={{ fontSize: "13px", color: "#64748b", marginTop: "2px" }}>{h.msg || "-"}</div>
          <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "4px" }}>{formatOrderDate(h.when)}</div>
        </div>
      ))}
    </div>
  );
}

export default function MyOrders() {
  const clientUcc = useMemo(() => TEST_CLIENT_CODE, []);

  const [orderType, setOrderType] = useState("system");
  const [transactionType, setTransactionType] = useState("purchase");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [viewType, setViewType] = useState("on_screen");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);

  const openCloseFilter = orderType === "system" ? "o" : "a";

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const types =
        transactionType === "sell" ? ["r"] : transactionType === "purchase" ? ["p"] : ["p", "r"];
      const payload = buildOrderListPayload({
        memberId: DEFAULT_MEMBER_ID,
        clientCode: clientUcc,
        openClose: openCloseFilter,
        types,
        start: 0,
        length: 100,
      });

      const res = await orderList(payload);
      const body = res?.data || {};
      if (body.status !== "success") {
        throw new Error(extractBseApiError({ response: { data: body } }));
      }

      let rows = parseOrderListResponse(body);
      const from = startDate ? new Date(`${startDate}T00:00:00`) : null;
      const to = endDate ? new Date(`${endDate}T23:59:59`) : null;
      if (from || to) {
        rows = rows.filter((row) => {
          if (!row.date) return true;
          const placed = new Date(row.date);
          if (Number.isNaN(placed.getTime())) return true;
          if (from && placed < from) return false;
          if (to && placed > to) return false;
          return true;
        });
      }

      setOrders(rows);
    } catch (err) {
      setOrders([]);
      setError(extractBseApiError(err));
    } finally {
      setLoading(false);
    }
  }, [clientUcc, openCloseFilter, transactionType, startDate, endDate]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleApply = () => {
    fetchOrders();
  };

  const handleViewOrder = async (row) => {
    setSelectedOrder({ orderNo: row.orderNo, loading: true });
    setDetailLoading(true);
    setDetailError("");
    try {
      const payload = buildOrderGetPayload(row.orderNo, openCloseFilter);
      const res = await orderGet(payload);
      const body = res?.data || {};
      if (body.status !== "success") {
        throw new Error(extractBseApiError({ response: { data: body } }));
      }
      const detail = parseOrderGetResponse(body);
      if (!detail) throw new Error("Order details not found");
      setSelectedOrder(detail);
    } catch (err) {
      setDetailError(extractBseApiError(err));
      setSelectedOrder({ orderNo: row.orderNo });
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setSelectedOrder(null);
    setDetailError("");
  };

  const handleCancelOrder = async (row) => {
    const orderId = row.orderNo;
    if (!window.confirm(`Cancel BSE order #${orderId}?`)) return;
    setCancelLoading(true);
    try {
      const res = await orderCancel(buildOrderCancelPayload(orderId));
      const body = res?.data || {};
      if (body.status !== "success") {
        throw new Error(extractBseApiError({ response: { data: body } }));
      }
      closeDetailModal();
      fetchOrders();
    } catch (err) {
      window.alert(extractBseApiError(err));
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <>
      <ClientNavbar />
      <div className="page-content" style={{ background: "#f1f5f9", minHeight: "100vh" }}>
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px" }}>
          <div className="card-body p-4">
            <h3 className="text-primary mb-2">My Orders</h3>
            <nav aria-label="breadcrumb" style={{ "--bs-breadcrumb-divider": "none" }}>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <NavLink to="/overview">Dashboard</NavLink> /
                </li>
                <li className="breadcrumb-item">
                  <span style={{ color: "#64748b" }}>Invest Online BSE</span> /
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  My Orders
                </li>
              </ol>
            </nav>
            <p className="small text-muted mb-0 mt-2">
              BSE UCC: <strong>{clientUcc}</strong> · API: <code>/api/bse/order_list</code>
            </p>
          </div>
        </div>

        <div
          className="card shadow-sm border-0 mb-4"
          style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}
        >
          <div className="card-body p-4">
            <div className="mb-4">
              <label className="form-label mb-2" style={{ color: "#334155", fontWeight: "500" }}>
                Order Type :
              </label>
              <div className="d-flex gap-4">
                <label className="d-flex align-items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="orderType"
                    checked={orderType === "system"}
                    onChange={() => setOrderType("system")}
                    style={radioStyle(orderType === "system")}
                  />
                  <span style={{ color: "#475569", fontSize: "14px" }}>Open Orders</span>
                </label>
                <label className="d-flex align-items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="orderType"
                    checked={orderType === "all"}
                    onChange={() => setOrderType("all")}
                    style={radioStyle(orderType === "all")}
                  />
                  <span style={{ color: "#475569", fontSize: "14px" }}>All Orders</span>
                </label>
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label mb-3" style={{ color: "#334155", fontWeight: "500" }}>
                Filters :
              </label>
              <div className="row g-3">
                <div className="col-12 col-md-4">
                  <label className="form-label small text-muted mb-1">Client UCC</label>
                  <input
                    type="text"
                    className="form-control"
                    value={clientUcc}
                    readOnly
                    style={inputStyle}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label small text-muted mb-1">Transaction Type</label>
                  <div className="d-flex gap-4 pt-2">
                    <label className="d-flex align-items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="transactionType"
                        checked={transactionType === "purchase"}
                        onChange={() => setTransactionType("purchase")}
                        style={radioStyle(transactionType === "purchase")}
                      />
                      <span style={{ color: "#475569", fontSize: "14px" }}>Purchase</span>
                    </label>
                    <label className="d-flex align-items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="transactionType"
                        checked={transactionType === "sell"}
                        onChange={() => setTransactionType("sell")}
                        style={radioStyle(transactionType === "sell")}
                      />
                      <span style={{ color: "#475569", fontSize: "14px" }}>Sell</span>
                    </label>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label small text-muted mb-1">Order Date</label>
                  <div
                    className="d-flex align-items-center gap-2 flex-wrap"
                    style={{ ...inputStyle, padding: "6px 12px" }}
                  >
                    <input
                      type="date"
                      className="form-control border-0 shadow-none p-0 bg-transparent"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      style={{ width: 130, outline: "none", fontSize: "14px" }}
                    />
                    <span style={{ color: "#64748b" }}>-</span>
                    <input
                      type="date"
                      className="form-control border-0 shadow-none p-0 bg-transparent"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      style={{ width: 130, outline: "none", fontSize: "14px" }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label mb-2" style={{ color: "#334155", fontWeight: "500" }}>
                View Type :
              </label>
              <div className="d-flex gap-4">
                <label className="d-flex align-items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="viewType"
                    checked={viewType === "on_screen"}
                    onChange={() => setViewType("on_screen")}
                    style={radioStyle(viewType === "on_screen")}
                  />
                  <span style={{ color: "#475569", fontSize: "14px" }}>On Screen</span>
                </label>
              </div>
            </div>

            <div className="d-flex justify-content-center pt-2">
              <button
                type="button"
                className="btn px-4 py-2"
                style={{
                  background: TEAL,
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: "600",
                  fontSize: "15px",
                }}
                onClick={handleApply}
                disabled={loading}
              >
                {loading ? "Loading..." : "Apply"}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div
          className="card shadow-sm border-0"
          style={{ borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden" }}
        >
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead style={{ background: "#f8fafc" }}>
                <tr>
                  <th style={{ padding: "12px 16px", color: "#475569", fontWeight: "600", fontSize: "13px" }}>
                    Order No
                  </th>
                  <th style={{ padding: "12px 16px", color: "#475569", fontWeight: "600", fontSize: "13px" }}>
                    Date
                  </th>
                  <th style={{ padding: "12px 16px", color: "#475569", fontWeight: "600", fontSize: "13px" }}>
                    Scheme
                  </th>
                  <th style={{ padding: "12px 16px", color: "#475569", fontWeight: "600", fontSize: "13px" }}>
                    Transaction Type
                  </th>
                  <th style={{ padding: "12px 16px", color: "#475569", fontWeight: "600", fontSize: "13px" }}>
                    Amount (₹)
                  </th>
                  <th style={{ padding: "12px 16px", color: "#475569", fontWeight: "600", fontSize: "13px" }}>
                    Status
                  </th>
                  <th style={{ padding: "12px 16px", color: "#475569", fontWeight: "600", fontSize: "13px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody style={{ background: "#fff", minHeight: 200 }}>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-5 text-muted">
                      Loading orders from BSE...
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-5">
                      <div className="d-flex flex-column align-items-center justify-content-center text-muted">
                        <i
                          className="fa fa-exclamation-triangle mb-2"
                          style={{ fontSize: "28px", color: "#f59e0b" }}
                          aria-hidden
                        />
                        <span style={{ fontSize: "15px" }}>No orders found for the selected filters.</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  orders.map((row) => (
                    <tr key={`${row.orderNo}-${row.memOrdRefId}`}>
                      <td style={{ padding: "12px 16px", fontSize: "14px" }}>{row.orderNo}</td>
                      <td style={{ padding: "12px 16px", fontSize: "14px" }}>{formatOrderDate(row.date)}</td>
                      <td style={{ padding: "12px 16px", fontSize: "14px" }}>
                        <div>{row.scheme}</div>
                        {row.schemeCode && (
                          <div className="small text-muted">{row.schemeCode}</div>
                        )}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: "14px" }}>{row.transactionType}</td>
                      <td style={{ padding: "12px 16px", fontSize: "14px" }}>{formatAmount(row.amount)}</td>
                      <td style={{ padding: "12px 16px", fontSize: "14px" }}>
                        <span className={`badge ${statusBadgeClass(row.status)}`}>{row.status}</span>
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: "14px" }}>
                        <div className="d-flex gap-2 flex-wrap">
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{
                              background: TEAL,
                              color: "#fff",
                              border: "none",
                              borderRadius: "6px",
                              fontSize: "13px",
                            }}
                            onClick={() => handleViewOrder(row)}
                            disabled={detailLoading && selectedOrder?.orderNo === row.orderNo}
                          >
                            {detailLoading && selectedOrder?.orderNo === row.orderNo ? "Loading..." : "View"}
                          </button>
                          {isOrderCancellable(row.status) && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              style={{ borderRadius: "6px", fontSize: "13px" }}
                              disabled={cancelLoading}
                              onClick={() => handleCancelOrder(row)}
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {selectedOrder && (
          <div
            className="modal fade show d-block"
            style={{ background: "rgba(15, 23, 42, 0.55)", backdropFilter: "blur(2px)" }}
            onClick={closeDetailModal}
          >
            <div
              className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="modal-content border-0 shadow-lg overflow-hidden"
                style={{ borderRadius: "14px", maxHeight: "90vh" }}
              >
                {/* Summary header */}
                <div
                  style={{
                    background: `linear-gradient(135deg, ${TEAL} 0%, #0891b2 100%)`,
                    padding: "20px 24px 18px",
                    color: "#fff",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <div style={{ fontSize: "12px", opacity: 0.85, fontWeight: "500", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                        Order Details
                      </div>
                      <div style={{ fontSize: "22px", fontWeight: "700", fontFamily: "ui-monospace, monospace", marginTop: "2px" }}>
                        #{selectedOrder.orderNo}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      onClick={closeDetailModal}
                      aria-label="Close"
                      style={{ opacity: 0.9 }}
                    />
                  </div>

                  {detailLoading ? (
                    <div style={{ opacity: 0.9, fontSize: "14px" }}>Fetching details...</div>
                  ) : detailError ? null : (
                    <div className="d-flex flex-wrap align-items-end justify-content-between gap-3">
                      <div>
                        <div style={{ fontSize: "28px", fontWeight: "700", lineHeight: 1.1 }}>
                          {formatAmount(selectedOrder.amount)}
                        </div>
                        <div style={{ fontSize: "13px", opacity: 0.9, marginTop: "4px" }}>
                          {selectedOrder.transactionType}
                          {selectedOrder.placedAt && ` · ${formatOrderDate(selectedOrder.placedAt)}`}
                        </div>
                      </div>
                      <span
                        className={`badge ${statusBadgeClass(selectedOrder.status)}`}
                        style={{ fontSize: "12px", padding: "6px 12px", borderRadius: "20px" }}
                      >
                        {formatStatusLabel(selectedOrder.status)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="modal-body p-4" style={{ background: "#f1f5f9" }}>
                  {detailLoading ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border mb-3"
                        style={{ color: TEAL, width: "2.5rem", height: "2.5rem" }}
                        role="status"
                      />
                      <div style={{ color: "#64748b", fontSize: "14px" }}>Loading order details from BSE...</div>
                    </div>
                  ) : detailError ? (
                    <div
                      className="d-flex align-items-start gap-3 p-3"
                      style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px" }}
                    >
                      <i className="fa fa-exclamation-circle text-danger mt-1" aria-hidden />
                      <div>
                        <div style={{ fontWeight: "600", color: "#991b1b", fontSize: "14px" }}>Could not load order</div>
                        <div style={{ color: "#b91c1c", fontSize: "13px", marginTop: "4px" }}>{detailError}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-3">
                      <DetailSection title="Order Information" icon="fa-file-text-o">
                        <div className="row g-3">
                          <div className="col-sm-6">
                            <DetailItem label="Order No" value={selectedOrder.orderNo} mono />
                          </div>
                          <div className="col-sm-6">
                            <DetailItem label="Member Ref ID" value={selectedOrder.memOrdRefId} mono />
                          </div>
                          <div className="col-sm-6">
                            <DetailItem label="Transaction Type" value={selectedOrder.transactionType} />
                          </div>
                          <div className="col-sm-6">
                            <DetailItem label="Placed At" value={formatOrderDate(selectedOrder.placedAt)} />
                          </div>
                          {selectedOrder.orderAction && (
                            <div className="col-sm-6">
                              <DetailItem label="Order Action" value={selectedOrder.orderAction} />
                            </div>
                          )}
                          {selectedOrder.schemeTat && (
                            <div className="col-sm-6">
                              <DetailItem label="Settlement TAT" value={selectedOrder.schemeTat} />
                            </div>
                          )}
                        </div>
                      </DetailSection>

                      <DetailSection title="Scheme & Investment" icon="fa-line-chart">
                        <div className="row g-3">
                          <div className="col-12">
                            <DetailItem label="Scheme" value={selectedOrder.scheme} highlight />
                          </div>
                          {selectedOrder.isin && (
                            <div className="col-sm-6">
                              <DetailItem label="ISIN" value={selectedOrder.isin} mono />
                            </div>
                          )}
                          <div className="col-sm-6">
                            <DetailItem label="Amount" value={formatAmount(selectedOrder.amount)} highlight />
                          </div>
                          <div className="col-sm-6">
                            <DetailItem label="Folio" value={selectedOrder.folio || "—"} />
                          </div>
                          <div className="col-sm-6">
                            <DetailItem label="Mode" value={selectedOrder.physOrDemat} />
                          </div>
                        </div>
                      </DetailSection>

                      <DetailSection title="Investor & Contact" icon="fa-user-o">
                        <div className="row g-3">
                          <div className="col-sm-6">
                            <DetailItem label="UCC" value={selectedOrder.ucc} mono />
                          </div>
                          {selectedOrder.euin && (
                            <div className="col-sm-6">
                              <DetailItem label="EUIN" value={selectedOrder.euin} mono />
                            </div>
                          )}
                          <div className="col-sm-6">
                            <DetailItem label="Email" value={selectedOrder.email} />
                          </div>
                          <div className="col-sm-6">
                            <DetailItem label="Mobile" value={selectedOrder.mobnum} />
                          </div>
                        </div>
                      </DetailSection>

                      {(selectedOrder.paymentRefId || selectedOrder.remarks || selectedOrder.rejectionReason) && (
                        <DetailSection title="Payment & Notes" icon="fa-credit-card">
                          <div className="row g-3">
                            {selectedOrder.paymentRefId && (
                              <div className="col-sm-6">
                                <DetailItem label="Payment Ref ID" value={selectedOrder.paymentRefId} mono />
                              </div>
                            )}
                            {selectedOrder.remarks && (
                              <div className="col-12">
                                <DetailItem label="Remarks" value={selectedOrder.remarks} />
                              </div>
                            )}
                            {selectedOrder.rejectionReason && (
                              <div className="col-12">
                                <div
                                  style={{
                                    padding: "10px 12px",
                                    background: "#fef2f2",
                                    borderRadius: "8px",
                                    border: "1px solid #fecaca",
                                  }}
                                >
                                  <DetailItem label="Rejection Reason" value={selectedOrder.rejectionReason} />
                                </div>
                              </div>
                            )}
                          </div>
                        </DetailSection>
                      )}

                      {selectedOrder.history?.length > 0 && (
                        <DetailSection title="Order Timeline" icon="fa-history">
                          <OrderHistoryTimeline history={selectedOrder.history} />
                        </DetailSection>
                      )}
                    </div>
                  )}
                </div>

                <div
                  className="modal-footer border-0 px-4 py-3"
                  style={{ background: "#fff", borderTop: "1px solid #e2e8f0" }}
                >
                  {!detailLoading && !detailError && isOrderCancellable(selectedOrder.status) && (
                    <button
                      type="button"
                      className="btn btn-outline-danger me-auto"
                      disabled={cancelLoading}
                      onClick={() => handleCancelOrder(selectedOrder)}
                    >
                      {cancelLoading ? "Cancelling…" : "Cancel order"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn px-4"
                    style={{
                      background: TEAL,
                      color: "#fff",
                      border: "none",
                      borderRadius: "8px",
                      fontWeight: "600",
                    }}
                    onClick={closeDetailModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
